/* eslint-disable max-lines */
import { toJS } from 'mobx';

import { BernieGraphqlClient, FetchPolicy } from 'bernie-graphql';
import { getErrorString, Logger, NOOP_LOGGER, SystemEvent } from 'bernie-logger';
import { Headers } from 'bernie-core/dist/source';
import { merge, SerializedData, timeoutPromise } from 'bernie-core';

import { SOURCE_EVENTS } from './events/source-events';

import { jsonToSplunkFriendlyString } from 'src/server/modules/web-server/middleware/flex/flex-middleware-utils.js';

import {
  AddPropertyToCart,
  ApiOperations,
  Cart,
  CreateMultiItemSession,
  CreateQuestion,
  CreateSessionCart,
  EmptyCart,
  InitiateMultiItemSession,
  LodgingPwaPropertySearch,
  LodgingPwaPropertySearchSortAndFilter,
  OperationResult,
  PrepareCheckout,
  PrepareCheckoutByCartId,
  PropertyAvailabilityCheck,
  PropertyInfoDetails,
  PropertyInfoOffersBexFeatures,
  PropertyInfoPageLayout,
  PropertyLocationScoreInfo,
  PropertyRateCalendar,
  PropertyRoomScoreInfo,
  PropertySearchTravelAds,
  QuestionAnswers,
  RemoveItemFromCart,
  SelectProducts,
  ShareInfo,
} from '__generated__/api';

const { withoutKeys } = merge;

// We want to eventually load this from config with the current git hash and environement
const CLIENT_INFO = 'shopping-pwa,unknown,unknown';

const buildBernieGraphQLClientWithHeaders = (uri: string, headers: Headers, logger: Logger): BernieGraphqlClient =>
  new BernieGraphqlClient(
    {
      uri: `${uri || '/graphql'}`,
      headers: { ...headers, 'Client-Info': CLIENT_INFO },
    },
    logger
  );

export class BexApiSource {
  private readonly api: ApiOperations;
  private readonly logger: Logger;

  constructor(host = '', logger: Logger = NOOP_LOGGER, headers: Headers = {}) {
    this.api = ApiOperations.withClient(buildBernieGraphQLClientWithHeaders(host, headers, logger));
    this.logger = logger;
  }

  public fetchPageLayoutTemplate(variables: PropertyInfoPageLayout.Variables) {
    return this.api
      .propertyInfoPageLayout(variables)
      .catch(this.getErrorHandler(variables, SOURCE_EVENTS.BEX_API_LODGING_PROPERTY_INFO_PAGE_LAYOUT_FETCH_ERROR));
  }

  public fetchPropertyInfoDetails(
    variables: PropertyInfoDetails.Variables,
    pageId: string,
    traceId: string
  ): Promise<OperationResult<PropertyInfoDetails.Query>> {
    const requestOptions = {
      pageId,
    };

    return this.api
      .propertyInfoDetails(variables, requestOptions)
      .catch(this.getErrorHandlerWithContext(variables, SOURCE_EVENTS.BEX_API_LODGING_PROPERTY_INFO_DETAILS_FETCH_ERROR, traceId));
  }

  public fetchPropertyInfoOffersBexFeatures(
    variables: PropertyInfoOffersBexFeatures.Variables,
    timeout: number
  ): Promise<OperationResult<PropertyInfoOffersBexFeatures.Query>> {
    return timeoutPromise(
      this.api.propertyInfoOffersBexFeatures(variables),
      timeout,
      `fetchPropertyInfoOffersBexFeatures timeout error`
    ).catch(this.getErrorHandler(variables, SOURCE_EVENTS.BEX_API_LODGING_PROPERTY_INFO_OFFERS_FETCH_ERROR));
  }

  public fetchPropertyAvailabilityCheck(
    variables: PropertyAvailabilityCheck.Variables
  ): Promise<OperationResult<PropertyAvailabilityCheck.Query>> {
    return this.api
      .propertyAvailabilityCheck(variables)
      .catch(this.getErrorHandler(variables, SOURCE_EVENTS.BEX_API_LODGING_PROPERTY_INFO_OFFERS_FETCH_ERROR));
  }

  public fetchSortAndFilter(
    variables: LodgingPwaPropertySearchSortAndFilter.Variables,
    pageId: string
  ): Promise<OperationResult<LodgingPwaPropertySearchSortAndFilter.Query>> {
    return this.api
      .lodgingPwaPropertySearchSortAndFilter(variables, {
        pageId,
        fetchPolicy: FetchPolicy.NO_CACHE,
      })
      .catch(this.getErrorHandler(variables, SOURCE_EVENTS.BEX_API_LODGING_PROPERTY_SEARCH_SORT_AND_FILTER_FETCH_ERROR));
  }

  public searchProperties(
    variables: LodgingPwaPropertySearch.Variables,
    pageId: string
  ): Promise<OperationResult<LodgingPwaPropertySearch.Query>> {
    const requestOptions = {
      pageId,
      fetchPolicy: FetchPolicy.NO_CACHE,
    };

    return this.api
      .lodgingPwaPropertySearch(variables, requestOptions)
      .catch(this.getErrorHandler(variables, SOURCE_EVENTS.BEX_API_LODGING_FULL_PROPERTY_SEARCH_FETCH_ERROR));
  }

  public fetchTravelAdPropertySearch(
    variables: LodgingPwaPropertySearch.Variables,
    pageId: string
  ): Promise<OperationResult<PropertySearchTravelAds.Query>> {
    const requestOptions = {
      pageId,
      fetchPolicy: FetchPolicy.NO_CACHE,
    };

    return this.api
      .propertySearchTravelAds(variables, requestOptions)
      .catch(this.getErrorHandler(variables, SOURCE_EVENTS.BEX_API_LODGING_TRAVELAD_SEARCH_FETCH_ERROR));
  }

  public fetchPropertyRoomScoreInfo(
    variables: PropertyRoomScoreInfo.Variables,
    pageId: string,
    timeout: number
  ): Promise<OperationResult<PropertyRoomScoreInfo.Query>> {
    const requestOptions = {
      pageId,
    };

    return timeoutPromise(this.api.propertyRoomScoreInfo(variables, requestOptions), timeout, 'PropertyRoomScoreInfo timeout error').catch(
      this.getErrorHandler(variables, SOURCE_EVENTS.BEX_API_UGC_ROOM_SCORE_FETCH_ERROR)
    );
  }

  public fetchPropertyLocationScoreInfo(
    variables: PropertyLocationScoreInfo.Variables,
    pageId: string,
    timeout: number
  ): Promise<OperationResult<PropertyLocationScoreInfo.Query>> {
    const requestOptions = {
      pageId,
    };

    return timeoutPromise(
      this.api.propertyLocationScoreInfo(variables, requestOptions),
      timeout,
      'PropertyLocationScoreInfo timeout error'
    ).catch(this.getErrorHandler(variables, SOURCE_EVENTS.BEX_API_UGC_LOCATION_SCORE_FETCH_ERROR));
  }

  public fetchShareInfo(variables: ShareInfo.Variables, pageId: string, timeout: number): Promise<OperationResult<ShareInfo.Query>> {
    const requestOptions = {
      pageId,
    };

    return timeoutPromise(this.api.shareInfo(variables, requestOptions), timeout, 'ShareInfo timeout error').catch(
      this.getErrorHandler(variables, SOURCE_EVENTS.BEX_API_LODGING_SHAREINFO_FETCH_ERROR)
    );
  }

  public createSessionCart(variables: CreateSessionCart.Variables): Promise<OperationResult<CreateSessionCart.Mutation>> {
    return this.api.createSessionCart(variables).catch(this.getErrorHandler(variables, SOURCE_EVENTS.BEX_API_MULTIITEM_CREATE_CART_ERROR));
  }

  public addPropertyToCart(variables: AddPropertyToCart.Variables): Promise<OperationResult<AddPropertyToCart.Mutation>> {
    return this.api
      .addPropertyToCart(variables)
      .catch(this.getErrorHandler(variables, SOURCE_EVENTS.BEX_API_MULTIITEM_ADD_ITEM_TO_CART_ERROR));
  }

  public createMultiItemSession(variables: CreateMultiItemSession.Variables): Promise<OperationResult<CreateMultiItemSession.Mutation>> {
    return this.api
      .createMultiItemSession(variables)
      .catch(this.getErrorHandler(variables, SOURCE_EVENTS.BEX_API_MULTIITEM_CREATE_MULTIITEM_SESSION));
  }

  public selectProducts(variables: SelectProducts.Variables): Promise<OperationResult<SelectProducts.Mutation>> {
    return this.api.selectProducts(variables).catch(this.getErrorHandler(variables, SOURCE_EVENTS.BEX_API_SELECT_PRODUCTS));
  }

  public initiateMultiItemSession(
    variables: InitiateMultiItemSession.Variables
  ): Promise<OperationResult<InitiateMultiItemSession.Mutation>> {
    return this.api
      .initiateMultiItemSession(variables)
      .catch(this.getErrorHandler(variables, SOURCE_EVENTS.BEX_API_INITIATE_MULTIITEM_SESSION));
  }

  public fetchCart(variables: Cart.Variables): Promise<OperationResult<Cart.Query>> {
    return this.api
      .cart(variables, FetchPolicy.NO_CACHE)
      .catch(this.getErrorHandler(variables, SOURCE_EVENTS.BEX_API_MULTIITEM_CART_FETCH_ERROR));
  }

  public removeItemFromCart(variables: RemoveItemFromCart.Variables): Promise<OperationResult<RemoveItemFromCart.Mutation>> {
    return this.api
      .removeItemFromCart(variables)
      .catch(this.getErrorHandler(variables, SOURCE_EVENTS.BEX_API_MULTIITEM_REMOVE_ITEM_FROM_CART_ERROR));
  }

  public emptyCart(variables: EmptyCart.Variables): Promise<OperationResult<EmptyCart.Mutation>> {
    return this.api
      .emptyCart(variables)
      .catch(this.getErrorHandler(variables, SOURCE_EVENTS.BEX_API_MULTIITEM_REMOVE_ITEM_FROM_CART_ERROR));
  }

  public prepareCheckout(variables: PrepareCheckout.Variables): Promise<OperationResult<PrepareCheckout.Mutation>> {
    return this.api
      .prepareCheckout(variables)
      .catch(this.getErrorHandler(variables, SOURCE_EVENTS.BEX_API_MULTIITEM_PREPARE_CHECKOUT_ERROR));
  }

  public prepareCheckoutByCartId(variables: PrepareCheckoutByCartId.Variables): Promise<OperationResult<PrepareCheckoutByCartId.Mutation>> {
    return this.api
      .prepareCheckoutByCartId(variables)
      .catch(this.getErrorHandler(variables, SOURCE_EVENTS.BEX_API_MULTIITEM_PREPARE_CHECKOUT_BY_CART_ID_ERROR));
  }

  public fetchQuestionAnswers(variables: QuestionAnswers.Variables): Promise<OperationResult<QuestionAnswers.Query>> {
    return this.api
      .questionAnswers(variables)
      .catch(this.getErrorHandler(variables, SOURCE_EVENTS.BEX_API_UGC_QUESTION_ANSWERS_FETCH_ERROR));
  }

  public createQuestion(variables: CreateQuestion.Variables): Promise<OperationResult<CreateQuestion.Mutation>> {
    return this.api.createQuestion(variables).catch(this.getErrorHandler(variables, SOURCE_EVENTS.BEX_API_UGC_CREATE_QUESTION_ERROR));
  }

  public fetchRateCalendar(
    variables: PropertyRateCalendar.Variables,
    pageId: string
  ): Promise<OperationResult<PropertyRateCalendar.Query>> {
    const requestOptions = {
      pageId,
    };

    return this.api
      .propertyRateCalendar(variables, requestOptions)
      .catch(this.getErrorHandler(variables, SOURCE_EVENTS.BEX_API_RATE_CALENDAR_FETCH_ERROR));
  }

  private getErrorHandler(variables: any, sourceEvent: SystemEvent) {
    return (error: any) => {
      this.logger.logEvent(sourceEvent, variables, getErrorString(error));
      // Throw the error to the next catch block since it is not resolved here
      throw error;
    };
  }

  private getErrorHandlerWithContext(variables: any, sourceEvent: SystemEvent, traceId: string) {
    const contextualData = {
      'trace-id': traceId,
    };

    return (error: any) => {
      this.logger.logEvent(sourceEvent, variables, getErrorString(error), jsonToSplunkFriendlyString(contextualData));
      // Throw the error to the next catch block since it is not resolved here
      throw error;
    };
  }

  /**
   * Override the default `toJson` to exclude the Apollo client.
   * If we don't include this, we will get a JSON serialization error.
   *
   * Creates a basic JSON object from the store, excluding
   * any fields provided in `ignoreAdditionalKeys`.
   */
  public toJSON(ignoreAdditionalKeys: string[] | string = []): SerializedData {
    if (!Array.isArray(ignoreAdditionalKeys)) {
      ignoreAdditionalKeys = ignoreAdditionalKeys.constructor === String ? [ignoreAdditionalKeys] : [];
    }

    const regexIgnorePrivate = /^_.+/;
    const simpleObject: any = toJS(this);
    const ignoreKeys: string[] = Object.keys(simpleObject)
      .filter(key => regexIgnorePrivate.test(key) || typeof simpleObject[key] === 'function')
      .concat(ignoreAdditionalKeys, 'logger', 'api', 'headers', 'apiWithFederation');

    return withoutKeys(ignoreKeys).merge({}, simpleObject);
  }
}
